import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { parsePhoneNumberFromString, AsYouType } from 'libphonenumber-js';
import {
  Dialog,
  DialogContent,
  Box,
  Stepper,
  Step,
  StepLabel,
  TextField,
  MenuItem,
  Stack,
  Alert,
  Avatar,
  Fade,
  InputAdornment,
  useTheme,
  useMediaQuery,
  LinearProgress,
} from '@mui/material';
import {
  Person,
  Business,
  Phone,
  LocationOn,
  CheckCircle,
  ArrowForward,
  ArrowBack,
  Save,
} from '@mui/icons-material';
import { useAuthContext } from '../../../contexts/AuthContext';
import { profileService } from '../services/profileService';
import { farmService, Farm } from '../../../services/farm/farmService';
import { asaasService } from '../../../services/asaasService';
import { Autocomplete, CircularProgress } from '@mui/material';
import { Button, Heading2, BodyText, Typography, FadeInView } from '../../../design-system';
import { DESIGN_TOKENS } from '../../../design-system/tokens/colors';
import { brazilianStates, brazilianStateLabels } from '../../support/types/supportTypes';
import { AnimatePresence, motion } from 'framer-motion';
import { useDebounce } from '../../../hooks/useDebounce';
import { normalizePaidPlanType } from '../../onboarding/services/onboardingService';

interface ProfileFormData {
  phone: string;
  country: SupportedCountry | '';
  business_type: string;
  company_name?: string;
  address?: string;
  addressNumber?: string;
  addressDistrict?: string;
  addressComplement?: string;
  city?: string;
  state?: string;
  postalCode?: string;
}

interface CompleteProfileModalProps {
  open: boolean;
  onSuccess?: () => void;
  onClose?: () => void;
}

type SupportedCountry = 'BR' | 'US';

const supportedCountries: Array<{ code: SupportedCountry; labelKey: string }> = [
  { code: 'BR', labelKey: 'complete_profile.countries.br' },
  { code: 'US', labelKey: 'complete_profile.countries.us' },
];

const usStates: Array<{ code: string; name: string }> = [
  { code: 'AL', name: 'Alabama' },
  { code: 'AK', name: 'Alaska' },
  { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' },
  { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' },
  { code: 'DE', name: 'Delaware' },
  { code: 'DC', name: 'District of Columbia' },
  { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' },
  { code: 'HI', name: 'Hawaii' },
  { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' },
  { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' },
  { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' },
  { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' },
  { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' },
  { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' },
  { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' },
  { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' },
  { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' },
  { code: 'PA', name: 'Pennsylvania' },
  { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' },
  { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' },
  { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' },
  { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' },
  { code: 'WY', name: 'Wyoming' },
];

const normalizePhoneToE164 = (raw: string, defaultCountry?: string) => {
  const value = raw?.trim();
  if (!value) return '';

  const parsedFromRaw = parsePhoneNumberFromString(value, defaultCountry as any);
  if (parsedFromRaw?.number) return parsedFromRaw.number;

  const digits = value.replace(/[^\d]/g, '');
  if (!digits) return value;

  const parsedWithPlus = parsePhoneNumberFromString(`+${digits}`, defaultCountry as any);
  if (parsedWithPlus?.number) return parsedWithPlus.number;

  const parsedFromDigits = parsePhoneNumberFromString(digits, defaultCountry as any);
  if (parsedFromDigits?.number) return parsedFromDigits.number;

  return value;
};

const formatCep = (value: string) => {
  const digits = String(value || '').replace(/\D/g, '').slice(0, 8);
  if (digits.length <= 5) return digits;
  return `${digits.slice(0, 5)}-${digits.slice(5)}`;
};

const formatAddressLine = (input: {
  street?: string;
  number?: string;
  complement?: string;
  district?: string;
}) => {
  const street = String(input.street || '').trim();
  const number = String(input.number || '').trim();
  const complement = String(input.complement || '').trim();
  const district = String(input.district || '').trim();

  const streetParts: string[] = [];
  if (street) streetParts.push(street);
  if (number) streetParts.push(number);

  let line = streetParts.join(', ');
  if (complement) line = line ? `${line} - ${complement}` : complement;
  if (district) line = line ? `${line}, ${district}` : district;

  return line;
};

const countryCodes = [
  { code: '+55', country: 'BR' },
  { code: '+1', country: 'US' },
  { code: '+351', country: 'PT' },
  { code: '+34', country: 'ES' },
  { code: '+44', country: 'GB' },
  { code: '+33', country: 'ES' },
  { code: '+49', country: 'DE' },
  { code: '+39', country: 'IT' },
  { code: '+54', country: 'AR' },
  { code: '+598', country: 'AR' },
  { code: '+595', country: 'UY' },
  { code: '+56', country: 'CL' },
  { code: '+57', country: 'CO' },
  { code: '+51', country: 'CO' },
  { code: '+52', country: 'PE' },
  { code: '+58', country: 'VE' },
];

export const CompleteProfileModal: React.FC<CompleteProfileModalProps> = ({ open, onSuccess, onClose }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useTranslation('profile');
  const { authState, checkUserProfile, refreshPlanStatus } = useAuthContext();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [profileInserted, setProfileInserted] = useState(false);
  
  const [phoneCode, setPhoneCode] = useState('+55');
  const [phoneNumber, setPhoneNumber] = useState('');

  const handlePhoneInputChange = (value: string) => {
    if (!value) {
      setPhoneNumber('');
      setFormData(prev => ({ ...prev, phone: '' }));
      return;
    }
    // Garante que sempre comece com "+" para o AsYouType interpretar o DDI
    const raw = value.startsWith('+') ? value : `+${value.replace(/[^\d]/g, '')}`;
    const formatter = new AsYouType();
    const formatted = formatter.input(raw);

    // Extrai o DDI detectado (pode ter 1 a 3 dígitos)
    const detectedCode = formatter.getCountry() ? `+${formatter.getCallingCode()}` : '+55';
    setPhoneCode(detectedCode);
    setPhoneNumber(formatted);
    setFormData(prev => ({ ...prev, phone: formatted }));
  };

  const [formData, setFormData] = useState<ProfileFormData>({
    phone: '',
    country: '',
    business_type: '',
    company_name: '',
    address: '',
    addressNumber: '',
    addressDistrict: '',
    addressComplement: '',
    city: '',
    state: '',
    postalCode: '',
  });

  const [addressQuery, setAddressQuery] = useState('');
  const [addressOptions, setAddressOptions] = useState<any[]>([]);
  const [loadingAddress, setLoadingAddress] = useState(false);
  const debouncedAddressQuery = useDebounce(addressQuery, 500);

  const [postalCode, setPostalCode] = useState('');
  const [cepLoading, setCepLoading] = useState(false);
  const [cepError, setCepError] = useState<string | null>(null);
  const lastCepFetchedRef = React.useRef<string>('');
  const hasManuallyEditedPostalCodeRef = React.useRef(false);

  const handlePostalCodeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    hasManuallyEditedPostalCodeRef.current = true;
    const next = formatCep(event.target.value);
    setPostalCode(next);
    setFormData((prev) => ({
      ...prev,
      postalCode: next.replace(/\D/g, ''),
    }));
    setCepError(null);

    const digits = next.replace(/\D/g, '');
    if (digits.length < 8) {
      lastCepFetchedRef.current = '';
    }
  };

  React.useEffect(() => {
    if (!open) return;
    hasManuallyEditedPostalCodeRef.current = false;
  }, [open]);

  // Search address using ViaCEP for Brazilian addresses
  React.useEffect(() => {
    const raw = postalCode || '';
    const query = raw.replace(/\D/g, '');

    if (query.length === 8 && query !== lastCepFetchedRef.current) {
      const timeoutId = setTimeout(() => {
        setCepLoading(true);
        setCepError(null);

        fetch(`https://viacep.com.br/ws/${query}/json/`)
          .then((res) => res.json())
          .then((data) => {
            if (data.erro) {
              setCepError(t('complete_profile.errors.cep_not_found'));
              return;
            }

            setFormData(prev => ({
              ...prev,
              address: data.logradouro || prev.address || '',
              addressComplement: data.complemento || prev.addressComplement || '',
              addressDistrict: data.bairro || prev.addressDistrict || '',
              city: data.localidade || prev.city || '',
              state: data.uf || prev.state || '',
              country: 'BR',
              postalCode: query,
            }));

            lastCepFetchedRef.current = query;
          })
          .catch((err) => {
            console.error('ViaCEP fetch error:', err);
            setCepError(t('complete_profile.errors.cep_fetch_failed'));
          })
          .finally(() => setCepLoading(false));
      }, 500);

      return () => clearTimeout(timeoutId);
    }
  }, [postalCode, t]);

  const handleAddressSelect = (_: any, value: any) => {
    if (!value) return;

    const { address } = value;
    const countryCode = address.country_code?.toUpperCase();
    
    // Map country
    let country: SupportedCountry = 'BR';
    if (countryCode === 'US') country = 'US';
    else if (countryCode === 'BR') country = 'BR';
    
    // Map state
    let state = '';
    if (country === 'BR') {
      // Nominatim returns full state name, we need to map to code (SP, RJ...)
      const stateName = address.state;
      // Try to find state code by name in brazilianStateLabels
      const entry = Object.entries(brazilianStateLabels).find(
        ([_, name]) => name.toLowerCase() === stateName?.toLowerCase()
      );
      if (entry) state = entry[0];
      else {
        // Fallback: check if state name is actually the code
        if (brazilianStates.includes(stateName)) state = stateName;
      }
    } else {
      // For US, we might get state name or code. Try to match with usStates
      const stateName = address.state;
      const usState = usStates.find(s => s.name.toLowerCase() === stateName?.toLowerCase() || s.code === stateName);
      if (usState) state = usState.code;
    }

    setFormData(prev => ({
      ...prev,
      address: value.display_name,
      city: address.city || address.town || address.village || '',
      state: state || prev.state, // Keep existing if not found
      country: country
    }));
  };

  const [showIntro, setShowIntro] = useState(true);

  const steps = [
    t('complete_profile.steps.contact'),
    t('complete_profile.steps.business'),
    t('complete_profile.steps.confirmation'),
  ];

  const businessTypes = [
    { value: 'individual', label: t('complete_profile.business_types.individual') },
    { value: 'farm_owner', label: t('complete_profile.business_types.farm_owner') },
    { value: 'cooperative', label: t('complete_profile.business_types.cooperative') },
    { value: 'company', label: t('complete_profile.business_types.company') },
    { value: 'consultant', label: t('complete_profile.business_types.consultant') },
    { value: 'veterinarian', label: t('complete_profile.business_types.veterinarian') },
    { value: 'other', label: t('complete_profile.business_types.other') },
  ];

  const defaultPhoneCountry = React.useMemo(() => {
    const browserLocale =
      (typeof navigator !== 'undefined' && (navigator.language || navigator.languages?.[0])) || '';
    const regionMatch = browserLocale.match(/-([A-Za-z]{2})$/);
    const fromBrowser = regionMatch?.[1]?.toUpperCase();

    if (fromBrowser) return fromBrowser;

    if (authState.user?.language_preference === 'pt') return 'BR';
    if (authState.user?.language_preference === 'es') return 'ES';
    return 'US';
  }, [authState.user?.language_preference]);

  const defaultSupportedCountry = React.useMemo<SupportedCountry>(() => {
    if (defaultPhoneCountry === 'BR') return 'BR';
    return 'US';
  }, [defaultPhoneCountry]);

  const selectedCountry = (formData.country || defaultSupportedCountry) as SupportedCountry;

  const stateOptions = React.useMemo<Array<{ value: string; label: string }>>(() => {
    if (selectedCountry === 'US') {
      return usStates.map((s) => ({ value: s.code, label: `${s.name} (${s.code})` }));
    }

    return brazilianStates.map((state) => ({ value: state, label: state }));
  }, [selectedCountry]);

  const countryLabel = React.useMemo(() => {
    return t(
      selectedCountry === 'US'
        ? 'complete_profile.countries.us'
        : 'complete_profile.countries.br'
    );
  }, [selectedCountry, t]);

  React.useEffect(() => {
    if (!open) return;

    if (!formData.country) {
      setFormData((prev) => ({ ...prev, country: defaultSupportedCountry }));
    }
  }, [defaultSupportedCountry, formData.country, open]);

  React.useEffect(() => {
    if (!formData.state) return;

    const isValid = stateOptions.some((opt) => opt.value === formData.state);
    if (!isValid) {
      setFormData((prev) => ({ ...prev, state: '' }));
    }
  }, [formData.state, stateOptions]);

  const handleCountryChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newCountry = (event.target.value as SupportedCountry) || defaultSupportedCountry;

    setFormData((prev) => ({
      ...prev,
      country: newCountry,
      state: '',
      phone: prev.phone ? normalizePhoneToE164(prev.phone, newCountry) : prev.phone,
    }));
    setError(null);
  };

  // Try to autofill from Asaas data if available
  React.useEffect(() => {
    const fetchAsaasData = async () => {
      // Ensure we have an email and the modal is open
      if (open && authState.user?.email) {
        try {
          const response: any = await asaasService.getCurrentAsaasCustomer(authState.user.email);
          const customer: any = response?.customer || response?.data?.customer || null;

          if (customer) {
            const incomingPostal = String(customer.postalCode || '').trim();
            const formattedPostal = incomingPostal ? formatCep(incomingPostal) : '';
            if (formattedPostal && !postalCode && !hasManuallyEditedPostalCodeRef.current) {
              setPostalCode(formattedPostal);
            }

            const customerState = `${customer.state || ''}`.trim().toUpperCase();
            const detectedCountry: SupportedCountry =
              usStates.some((s) => s.code === customerState)
                ? 'US'
                : brazilianStates.includes(customerState)
                  ? 'BR'
                  : defaultSupportedCountry;

            const rawPhone = String(customer.mobilePhone || customer.phone || '').trim();
            const normalizedPhone = rawPhone ? normalizePhoneToE164(rawPhone, detectedCountry) : '';

            if (normalizedPhone && !formData.phone) {
              handlePhoneInputChange(normalizedPhone);
            }

             setFormData(prev => {
                return {
                 ...prev,
                 country: prev.country || detectedCountry,
                 postalCode: prev.postalCode || incomingPostal.replace(/\D/g, ''),
                 address: prev.address || String(customer.address || ''),
                 addressNumber: prev.addressNumber || String(customer.addressNumber || ''),
                 addressDistrict: prev.addressDistrict || String(customer.province || ''),
                 addressComplement: prev.addressComplement || String(customer.complement || ''),
                 city: prev.city || customer.cityName || customer.city || '',
                 state: prev.state || customerState || '',
                };
             });
          }
        } catch (err) {
          // Silent fail for autofill
        }
      }
    };

    fetchAsaasData();
  }, [open, authState.user?.email, defaultSupportedCountry, postalCode, phoneNumber]);

  const handleStart = () => {
    setShowIntro(false);
  };

  const handleInputChange = (field: keyof ProfileFormData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData(prev => ({ ...prev, [field]: event.target.value }));
    setError(null);
  };

  const handleNext = () => {
    if (activeStep === 0) {
        if (!formData.phone) {
            setError(t('complete_profile.errors.phone_required'));
            return;
        }
        if (!formData.country) {
            setError(t('complete_profile.errors.country_required'));
            return;
        }
        if (!formData.city || !formData.state) {
            setError(t('complete_profile.errors.city_state_required'));
            return;
        }
    }
    if (activeStep === 1 && !formData.business_type) {
      setError(t('complete_profile.errors.business_type_required'));
      return;
    }
    setError(null);
    setActiveStep(prev => prev + 1);
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
    setError(null);
  };

  const handleSubmit = async () => {
    if (!authState.user?.username) {
      setError(t('complete_profile.errors.user_not_authenticated'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (!profileInserted) {
        const profileData = {
          username: authState.user.username,
          profile_type: 'individual',
          contact_details: {
            phone: formData.phone,
            email: authState.user.email,
            address: formatAddressLine({
              street: formData.address,
              number: formData.addressNumber,
              complement: formData.addressComplement,
              district: formData.addressDistrict,
            }),
            city: formData.city || '',
            state: formData.state || '',
            country: formData.country || '',
            postal_code: formData.postalCode || postalCode.replace(/\D/g, ''),
          },
          business_info: {
            business_type: formData.business_type,
            company_name: formData.company_name || '',
          },
          preferences: {
            language: authState.user.language_preference || 'pt',
            timezone: authState.user.timezone || 'America/Sao_Paulo',
          },
        };

        const result = await profileService.insert({ data: profileData });

        if (!result.success) {
          throw new Error(result.error || t('complete_profile.errors.create_profile_failed'));
        }

        setProfileInserted(true);

        let profileStatus = { hasProfile: false, isComplete: false, profileId: null as string | null };
        if (checkUserProfile) {
          profileStatus = await checkUserProfile(authState.user.username);
        }

        const planTypeFromState = normalizePaidPlanType(authState.planType || authState.user?.planType || null);
        let effectivePlanType = planTypeFromState;
        if (!effectivePlanType && refreshPlanStatus) {
          const refreshedPlan = await refreshPlanStatus();
          effectivePlanType = normalizePaidPlanType(refreshedPlan.planType || null);
        }

        const isProfessionalPlan = effectivePlanType === 'professional';
        if (!isProfessionalPlan) {
          if (onSuccess) onSuccess();
          return;
        }

        const ownerId = profileStatus.profileId;

        if (!ownerId) {
          setError(t('complete_profile.errors.profile_id_missing'));
          return;
        }

        const sandboxFarm: Omit<Farm, 'id' | 'created_at' | 'updated_at'> = {
          owner_id: ownerId,
          name: 'Sandbox',
          farm_type: ['mixed'],
          production_focus: 'mixed_production',
          country_code: formData.country || 'BR',
          registration_number: '',
          location: {
            address: formatAddressLine({
              street: formData.address,
              number: formData.addressNumber,
              complement: formData.addressComplement,
              district: formData.addressDistrict,
            }),
            region: formData.state || '',
            postal_code: formData.postalCode || postalCode.replace(/\D/g, ''),
            coordinates: { latitude: 0, longitude: 0 }
          },
          area_hectares: 0,
          capacity_head: 0,
          facilities: [],
          certifications: [],
          contact_info: {
            phone: formData.phone || '',
            email: authState.user.email || '',
            emergency_contact: ''
          },
          is_active: true,
          is_favorite: true
        };

        const sandboxResult = await farmService.createFarm(sandboxFarm);

        if (!sandboxResult.success) {
          setError(t('complete_profile.errors.create_sandbox_failed'));
          return;
        }

        if (typeof window !== 'undefined') {
          window.dispatchEvent(new Event('muu:organizations:refresh'));
        }
      }

      if (onSuccess) onSuccess();
    } catch (err: any) {
      setError(err.message || t('complete_profile.errors.complete_profile_failed'));
    } finally {
      setLoading(false);
    }
  };

  // Simple mask function for phone input
  const formatPhoneNumber = (value: string) => {
    // Remove non-digits
    const numbers = value.replace(/\D/g, '');
    
    // Apply mask based on length
    // (XX) XXXXX-XXXX for mobile (11 digits)
    // (XX) XXXX-XXXX for landline (10 digits)
    if (numbers.length <= 10) {
      return numbers.replace(/^(\d{2})(\d{4})(\d{0,4})/, '($1) $2-$3');
    } else {
      return numbers.replace(/^(\d{2})(\d{5})(\d{0,4})/, '($1) $2-$3');
    }
  };

  const handlePhoneChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = event.target.value;
    // Only allow backspace if it's already empty or just formatting
    if (rawValue.length < phoneNumber.length) {
       // Handle deletion logic if needed, but simple replace usually works for standard text fields
    }
    
    // Remove formatting to get raw numbers
    const numbers = rawValue.replace(/\D/g, '');
    
    // Limit to 11 digits (BR standard)
    if (numbers.length > 11) return;
    
    const formatted = formatPhoneNumber(numbers);
    setPhoneNumber(formatted);
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Stack spacing={3} sx={{ mt: 2 }}>
            <Box>
                <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                    {t('complete_profile.contact.title')}
                </Typography>
                <BodyText color="secondary" sx={{ mb: 3 }}>
                    {t('complete_profile.contact.description')}
                </BodyText>
            </Box>

            <TextField
              fullWidth
              label={t('complete_profile.contact.phone_label')}
              value={formData.phone || ''}
              onChange={(e) => handlePhoneInputChange(e.target.value)}
              placeholder="+55 19 99331-1803"
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Phone color="action" />
                  </InputAdornment>
                ),
              }}
            />
            
            <TextField
              fullWidth
              label={t('complete_profile.contact.zipcode')}
              value={postalCode}
              onChange={handlePostalCodeChange}
              placeholder="00000-000"
              error={!!cepError}
              helperText={cepError || ''}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LocationOn color="action" />
                  </InputAdornment>
                ),
                endAdornment: cepLoading ? (
                  <InputAdornment position="end">
                    <CircularProgress color="inherit" size={20} />
                  </InputAdornment>
                ) : null,
              }}
              inputProps={{ maxLength: 9 }}
            />

            <TextField
              fullWidth
              label={t('complete_profile.contact.street')}
              value={formData.address || ''}
              onChange={handleInputChange('address')}
              placeholder={t('complete_profile.contact.street_placeholder')}
            />

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                fullWidth
                label={t('complete_profile.contact.number')}
                value={formData.addressNumber || ''}
                onChange={handleInputChange('addressNumber')}
                placeholder={t('complete_profile.contact.number_placeholder')}
              />
              <TextField
                fullWidth
                label={t('complete_profile.contact.complement')}
                value={formData.addressComplement || ''}
                onChange={handleInputChange('addressComplement')}
                placeholder={t('complete_profile.contact.complement_placeholder')}
              />
            </Stack>

            <TextField
              fullWidth
              label={t('complete_profile.contact.district')}
              value={formData.addressDistrict || ''}
              onChange={handleInputChange('addressDistrict')}
              placeholder={t('complete_profile.contact.district_placeholder')}
            />

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                fullWidth
                label={t('complete_profile.contact.city')}
                value={formData.city || ''}
                onChange={handleInputChange('city')}
                placeholder={t('complete_profile.contact.city_placeholder')}
              />
              <TextField
                fullWidth
                select
                label={t('complete_profile.contact.state')}
                value={formData.state || ''}
                onChange={handleInputChange('state')}
              >
                {stateOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>

            {/* Hidden fields for validation/fallback display if needed */}
            {/* You can uncomment these if you want to allow manual edits after selection */}
            {/*
            <Stack spacing={2}>
              <TextField ... />
            </Stack>
            */}
          </Stack>
        );
      
      case 1:
        return (
          <Stack spacing={3} sx={{ mt: 2 }}>
             <Box>
                <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                    {t('complete_profile.business.title')}
                </Typography>
                <BodyText color="secondary" sx={{ mb: 3 }}>
                    {t('complete_profile.business.description')}
                </BodyText>
            </Box>

            <TextField
              fullWidth
              select
              label={`${t('complete_profile.business.business_type')} *`}
              value={formData.business_type}
              onChange={handleInputChange('business_type')}
              InputProps={{
                startAdornment: (
                    <InputAdornment position="start">
                        <Business color="action" />
                    </InputAdornment>
                ),
              }}
            >
              {businessTypes.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              fullWidth
              label={t('complete_profile.business.company_name_optional')}
              value={formData.company_name}
              onChange={handleInputChange('company_name')}
              placeholder={t('complete_profile.business.company_name_placeholder')}
            />
          </Stack>
        );
      
      case 2:
        return (
          <Box textAlign="center" sx={{ mt: 2 }}>
            <Avatar
              sx={{
                width: 80,
                height: 80,
                bgcolor: DESIGN_TOKENS.colors.primary[100],
                color: DESIGN_TOKENS.colors.primary[600],
                mx: 'auto',
                mb: 3,
              }}
            >
              <CheckCircle sx={{ fontSize: 40 }} />
            </Avatar>
            
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 700 }}>
              {t('complete_profile.confirmation.title')}
            </Typography>
            <BodyText color="secondary" sx={{ mb: 4 }}>
              {t('complete_profile.confirmation.description')}
            </BodyText>

            <Box sx={{ 
                bgcolor: DESIGN_TOKENS.colors.background.elevated, 
                p: 3, 
                borderRadius: 2,
                textAlign: 'left',
                border: `1px solid ${DESIGN_TOKENS.colors.neutral[200]}`
            }}>
              <Stack spacing={2}>
                  <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2" color="secondary">{t('complete_profile.confirmation.summary.name')}</Typography>
                      <Typography variant="body2" fontWeight={600}>{authState.user?.full_name}</Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2" color="secondary">{t('complete_profile.confirmation.summary.phone')}</Typography>
                      <Typography variant="body2" fontWeight={600}>{formData.phone}</Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2" color="secondary">{t('complete_profile.confirmation.summary.location')}</Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {formData.city} - {formData.state} ({countryLabel})
                      </Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2" color="secondary">{t('complete_profile.confirmation.summary.activity')}</Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {businessTypes.find(option => option.value === formData.business_type)?.label}
                      </Typography>
                  </Box>
              </Stack>
            </Box>
          </Box>
        );
      
      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth="sm"
      fullScreen={fullScreen}
      PaperProps={{
        sx: {
          borderRadius: fullScreen ? 0 : 3,
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          overflowY: 'visible', // Permite que o select overflow funcione melhor
        }
      }}
      slotProps={{
        backdrop: {
            sx: {
                backdropFilter: 'blur(8px)',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
            }
        }
      }}
    >
      <Box sx={{ position: 'relative', overflow: 'hidden' }}>
        
        <DialogContent
          sx={{
            p: { xs: 3, md: 5 },
            maxHeight: fullScreen ? '100vh' : '85vh',
            overflowY: 'auto',
          }}
        >
          {showIntro ? (
            <FadeInView>
              <Box textAlign="center" py={4}>
                <Avatar
                  sx={{
                    width: 100,
                    height: 100,
                    bgcolor: DESIGN_TOKENS.colors.primary[50],
                    color: DESIGN_TOKENS.colors.primary[600],
                    mx: 'auto',
                    mb: 4,
                  }}
                >
                  <Person sx={{ fontSize: 56 }} />
                </Avatar>
                
                <Typography variant="h4" sx={{ fontWeight: 800, color: DESIGN_TOKENS.colors.text.primary, mb: 2 }}>
                  {t('complete_profile.intro.title')}
                </Typography>
                
                <BodyText color="secondary" sx={{ mb: 6, maxWidth: 400, mx: 'auto' }}>
                  {t('complete_profile.intro.description')}
                </BodyText>

                <Stack spacing={2} alignItems="center">
                  <Button
                    onClick={handleStart}
                    variant="primary"
                    size="large"
                    endIcon={<ArrowForward />}
                    sx={{ px: 6, py: 1.5, fontSize: '1.1rem', width: '100%', maxWidth: 300 }}
                  >
                    {t('complete_profile.intro.start')}
                  </Button>

                  {onClose && (
                     <Button
                       onClick={onClose}
                       variant="ghost"
                       color="inherit"
                       sx={{ color: DESIGN_TOKENS.colors.text.secondary, border: 'none' }}
                     >
                       {t('complete_profile.intro.skip')}
                     </Button>
                   )}
                </Stack>
              </Box>
            </FadeInView>
          ) : (
            <FadeInView>
              <Box>
                {/* Header */}
                <Box mb={5}>
                  <Box display="flex" justifyContent="center" mb={4}>
                     <Stack direction="row" spacing={1} alignItems="center">
                        {steps.map((label, index) => {
                           const isActive = index === activeStep;
                           const isCompleted = index < activeStep;
                           
                           return (
                             <React.Fragment key={label}>
                               <Box 
                                 sx={{ 
                                   display: 'flex', 
                                   flexDirection: 'column', 
                                   alignItems: 'center', 
                                   position: 'relative',
                                   width: 100
                                 }}
                               >
                                 <motion.div
                                   initial={false}
                                   animate={{
                                     backgroundColor: isActive || isCompleted ? DESIGN_TOKENS.colors.primary[500] : DESIGN_TOKENS.colors.neutral[200],
                                     scale: isActive ? 1.1 : 1,
                                   }}
                                   style={{
                                     width: 32,
                                     height: 32,
                                     borderRadius: '50%',
                                     display: 'flex',
                                     alignItems: 'center',
                                     justifyContent: 'center',
                                     color: 'white',
                                     fontWeight: 600,
                                     fontSize: '0.875rem',
                                     marginBottom: 8,
                                     zIndex: 2
                                   }}
                                 >
                                   {isCompleted ? <CheckCircle sx={{ fontSize: 18 }} /> : index + 1}
                                 </motion.div>
                                 <Typography 
                                   variant="caption" 
                                   sx={{ 
                                     color: isActive ? DESIGN_TOKENS.colors.primary[600] : DESIGN_TOKENS.colors.text.secondary,
                                     fontWeight: isActive ? 600 : 400
                                   }}
                                 >
                                   {label}
                                 </Typography>

                                 {/* Connector Line */}
                                 {index < steps.length - 1 && (
                                   <Box 
                                     sx={{ 
                                       position: 'absolute', 
                                       top: 15, 
                                       left: '50%', 
                                       width: '100%', 
                                       height: 2, 
                                       bgcolor: index < activeStep ? DESIGN_TOKENS.colors.primary[500] : DESIGN_TOKENS.colors.neutral[200],
                                       zIndex: 1
                                     }} 
                                   />
                                 )}
                               </Box>
                             </React.Fragment>
                           );
                        })}
                     </Stack>
                  </Box>

                  <Typography variant="h5" sx={{ fontWeight: 700, color: DESIGN_TOKENS.colors.text.primary, textAlign: 'center' }}>
                      {steps[activeStep]}
                  </Typography>
                </Box>

                {/* Alerts */}
                <Fade in={!!error}>
                  <Box sx={{ mb: 2 }}>
                      {error && <Alert severity="error">{error}</Alert>}
                  </Box>
                </Fade>

                {/* Content */}
                <Box sx={{ minHeight: 400, overflow: 'visible' }}>
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={activeStep}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      transition={{ duration: 0.3 }}
                    >
                      {renderStepContent()}
                    </motion.div>
                  </AnimatePresence>
                </Box>

                {/* Actions */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4, pt: 2, borderTop: `1px solid ${DESIGN_TOKENS.colors.neutral[200]}` }}>
                  <Button
                    disabled={activeStep === 0 || loading}
                    onClick={handleBack}
                    variant="secondary"
                    startIcon={<ArrowBack />}
                  >
                    {t('complete_profile.actions.back')}
                  </Button>
                  
                  {activeStep === steps.length - 1 ? (
                    <Button
                      onClick={handleSubmit}
                      variant="primary"
                      disabled={loading}
                      startIcon={loading ? undefined : <Save />}
                      loading={loading}
                    >
                      {loading ? t('complete_profile.actions.saving') : t('complete_profile.actions.finish')}
                    </Button>
                  ) : (
                    <Button
                      onClick={handleNext}
                      variant="primary"
                      endIcon={<ArrowForward />}
                    >
                      {t('complete_profile.actions.continue')}
                    </Button>
                  )}
                </Box>
              </Box>
            </FadeInView>
          )}
        </DialogContent>
      </Box>
    </Dialog>
  );
};
